import { useMutation } from '@apollo/client';
import type { DraftAccount } from '@interfaces/account';
import {
  AccountStates,
  AccountTypes,
  ApplyForGovFunds,
} from '@interfaces/account';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import { Gender } from '@interfaces/user';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { Form } from '@ovComponents/2-component/form/form';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import {
  GoalLinkingInput,
  getValidGoals,
} from '@ovComponents/4-module/workflowCompletion/subSteps/account/goalLinking';
import {
  type FieldOptions,
  type FormErrors,
  validateFields,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { colors } from '@ovComponents/legacyColor';
import { UPDATE_USER } from '@pages/accountsDetail/components/newAffiliate';
import { Box, Button, Grid, Typography } from 'hephaestus/web';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type RespAdultDialogContentInterface = {
  userData: any;
  showGoalLinkingSection: boolean;
  onAccountCreated: (account: DraftAccount) => void;
  onDialogClose: () => void;
};

export const RespAdultDialogContent = ({
  userData,
  showGoalLinkingSection,
  onAccountCreated,
  onDialogClose,
}: RespAdultDialogContentInterface) => {
  const { t } = useTranslation(['workflowCompletions', 'affiliationTypes']);
  const [applyForGovFunds, setApplyForGovFunds] = useState<ApplyForGovFunds[]>(
    [],
  );
  const [gender, setGender] = useState<string>(userData.gender ?? '');
  const [goalsLinked, setGoalsLinked] = useState<Goal[]>([]);
  const [errors, setErrors] = useState<FormErrors>(null);
  const [focused, setFocused] = useState<string[]>([]);
  const [updateUser] = useMutation(UPDATE_USER);
  const goals = getValidGoals(userData);
  const handleApplyForGovFundSelect = (
    govFund: ApplyForGovFunds,
    checked: boolean,
  ) => {
    setFocused([...focused, 'applyForGovFunds']);
    if (checked) {
      setApplyForGovFunds((prevState) => [...prevState, govFund]);
    } else {
      setApplyForGovFunds([
        ...applyForGovFunds.filter((item) => item !== govFund),
      ]);
    }
  };

  const fieldOptions: FieldOptions = useMemo(
    () => ({
      applyForGovFunds: { required: true },
      gender: { required: true },
      goalsLinked: { required: false },
    }),
    [],
  );

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const data = {
        applyForGovFunds,
        gender,
        goalsLinked,
      };
      const newErrors = validateFields(fieldOptions, data, candidateFields);
      setErrors(newErrors);
      return newErrors;
    },
    [applyForGovFunds, fieldOptions, gender, goalsLinked],
  );

  const submit = async () => {
    const formErrors = validate();
    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }
    if (userData.gender !== gender) {
      await updateUser({
        variables: {
          input: {
            userId: userData.id,
            gender,
          },
        },
      }).then();
    }
    onAccountCreated({
      type: AccountTypes.RESP_ADULT,
      applyForGovFunds,
      id: '',
      state: AccountStates.ACTIVE,
      user: {
        ...userData,
        gender,
      },
      affiliations: [],
      goalsLinked,
    });
  };

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  return (
    <Form onSubmit={submit}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <Box
          onClick={onDialogClose}
          sx={{
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Icon icon={CloseRoundedIcon} size='medium' />
        </Box>
      </Box>
      <Typography variant='headingMedium' sx={{ mt: 1 }}>
        {t('workflowCompletions:respAccountCreation.respAdultTitle')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          mt: 3,
        }}
      >
        <Typography variant='bodyLarge' sx={{ mr: 0.5 }}>
          {t('workflowCompletions:respAccountCreation.subtitle')}
        </Typography>
      </Box>
      <Grid
        container
        spacing={1}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'end',
          mt: 2,
        }}
      >
        <Grid size={7}>
          <CheckboxField
            label={t(
              'workflowCompletions:respAccountCreation.basicCanadaEducationSavingsGrant',
            )}
            checked={applyForGovFunds.includes(ApplyForGovFunds.BASIC_CESG)}
            onChange={(checked) =>
              handleApplyForGovFundSelect(ApplyForGovFunds.BASIC_CESG, checked)
            }
          />
        </Grid>
        <Grid size={5}>
          <CheckboxField
            label={t(
              'workflowCompletions:respAccountCreation.canadaLearningBond',
            )}
            checked={applyForGovFunds.includes(ApplyForGovFunds.CLB)}
            onChange={(checked) =>
              handleApplyForGovFundSelect(ApplyForGovFunds.CLB, checked)
            }
          />
        </Grid>
      </Grid>
      {errors?.applyForGovFunds && (
        <HelpText tone='error' text={errors?.applyForGovFunds?.message} />
      )}
      <Box
        sx={{
          height: '1px',
          mt: 3,
          bgcolor: colors.neutral40,
        }}
      />
      <SelectField
        sx={{ mt: 3 }}
        onChange={(e: any) => setGender(e.target.value)}
        disabled={false}
        label={t('workflowCompletions:respAccountCreation.gender')}
        fullWidth
        onBlur={() => setFocused([...focused, 'gender'])}
        value={gender}
        errorText={errors?.gender?.message}
        error={!!errors?.gender}
      >
        {Object.values(Gender).map((g) => (
          <MenuItem value={g}>{t(`shared:genderOptions.${g}`)}</MenuItem>
        ))}
      </SelectField>
      <Box
        sx={{
          height: '1px',
          mt: 3,
          bgcolor: colors.neutral40,
        }}
      />
      {showGoalLinkingSection && (
        <Box sx={{ mt: 4 }}>
          <GoalLinkingInput
            goals={goals}
            goalsLinked={goalsLinked}
            setGoalsLinked={setGoalsLinked}
            errorText={errors?.goalsLinked?.message}
            error={!!errors?.goalsLinked}
          />
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button color='primary' sx={{ mt: 3 }} type='submit'>
          {t('workflowCompletions:respAccountCreation.addAccount')}
        </Button>
      </Box>
    </Form>
  );
};

export default RespAdultDialogContent;
